import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { 
  createUserInsurance as createUserInsuranceApi,
  CreateUserInsuranceRequest,
  UserInsuranceRecord
} from '../../api/user-insurance';

interface UserInsuranceState {
  loading: boolean;
  error: string | null;
  createdRecord: UserInsuranceRecord | null;
  submitSuccess: boolean;
}

const initialState: UserInsuranceState = {
  loading: false,
  error: null,
  createdRecord: null,
  submitSuccess: false,
};

interface CreateUserInsurancePayload {
  data: CreateUserInsuranceRequest;
  language: string;
}

export const createUserInsurance = createAsyncThunk(
  'userInsurance/createUserInsurance',
  async ({ data, language }: CreateUserInsurancePayload, { rejectWithValue }) => {
    try {
      const response = await createUserInsuranceApi(data, language);
      if (response.statusCode === 200 || response.statusCode === 201) {
        toast.success(response.message);
        return response.response;
      }
      const errorMsg = response.message || 'Failed to register assignment';
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    } catch (error: any) {
      console.log('Error caught in createUserInsurance:', error);
      console.log('Error response:', error?.response);
      console.log('Error response data:', error?.response?.data);
      
      let errorMsg = 'Failed to register assignment';
      
      if (error?.response?.data) {
        const responseData = error.response.data;
        
        // Handle ASP.NET Core validation errors format
        if (responseData.errors && typeof responseData.errors === 'object') {
          // Extract all validation error messages
          const validationErrors = Object.values(responseData.errors)
            .flat()
            .join(', ');
          errorMsg = validationErrors || responseData.title || errorMsg;
        }
        // Handle custom backend error format
        else if (responseData.message || responseData.Message) {
          errorMsg = responseData.message || responseData.Message;
        }
        // Handle generic error with title
        else if (responseData.title) {
          errorMsg = responseData.title;
        }
      } else if (error?.message) {
        // Generic error message
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

const userInsuranceSlice = createSlice({
  name: 'userInsurance',
  initialState,
  reducers: {
    resetUserInsuranceState: (state) => {
      state.loading = false;
      state.error = null;
      state.createdRecord = null;
      state.submitSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createUserInsurance.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.submitSuccess = false;
      })
      .addCase(createUserInsurance.fulfilled, (state, action) => {
        state.loading = false;
        state.createdRecord = action.payload;
        state.submitSuccess = true;
      })
      .addCase(createUserInsurance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.submitSuccess = false;
      });
  },
});

export const { resetUserInsuranceState } = userInsuranceSlice.actions;
export default userInsuranceSlice.reducer;
